import { Alert, Box, Button, Chip, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentHangout, useMyEntry, useStandings } from '../hooks/useHangout';
import { useCreateMarket } from '../hooks/useMarkets';
import { formatTokens } from '../lib/format';
import { MINIMUM_STAKE, type MarketKind } from '../schemas/market.schema';
import { getErrorMessage } from '../services/api-client';
import { useAuthStore } from '../stores/authStore';
import { colors, mono } from '../themes/theme';

// Only the shapes that need no foreknowledge of the answer. An over/under has
// to be given a line up front, which means guessing the answer before anyone
// has seen it -- and a closest-guess covers the same ground without that.
const KINDS: Array<{ value: MarketKind; label: string; hint: string }> = [
    { value: 'binary', label: 'Yes or no', hint: 'Does it happen or not?' },
    { value: 'field', label: 'Who wins', hint: 'Back one person out of a few' },
    { value: 'closest', label: 'Closest guess', hint: 'Everyone names a number' },
    { value: 'free_for_all', label: 'Everyone in', hint: 'Same buy-in, winner takes it' },
];

const CLOSE_PRESETS = [
    { label: 'A couple minutes', seconds: 120 },
    { label: '5 minutes', seconds: 300 },
    { label: '15 minutes', seconds: 900 },
    { label: 'An hour', seconds: 3600 },
] as const;

const MAX_CLOSE_SECONDS = 2_592_000;

/** `datetime-local` wants a local-time string with no zone, minutes precision. */
const toLocalInputValue = (date: Date): string => {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
};

export function NewMarket() {
    const navigate = useNavigate();
    const session = useAuthStore((s) => s.session);
    const hangout = useCurrentHangout();
    const entry = useMyEntry(hangout.data?.id);
    const standings = useStandings(hangout.data?.id);
    const create = useCreateMarket();

    const [text, setText] = useState('');
    const [kind, setKind] = useState<MarketKind>('binary');
    const [closesInSeconds, setClosesIn] = useState<number>(300);
    const [customClose, setCustomClose] = useState('');
    const [useCustomClose, setUseCustomClose] = useState(false);
    const [buyIn, setBuyIn] = useState('');
    const [entrantIds, setEntrantIds] = useState<number[]>([]);

    const cap = entry.data?.maxStakePerMarket ?? 1000;
    const others = (standings.data ?? []).filter((row) => row.userId !== session?.id);

    // Seconds from now to the picked moment, or the chosen preset.
    const customSeconds = customClose
        ? Math.round((new Date(customClose).getTime() - Date.now()) / 1000)
        : 0;
    const customValid =
        useCustomClose && customSeconds >= 30 && customSeconds <= MAX_CLOSE_SECONDS;
    const effectiveClose = useCustomClose ? customSeconds : closesInSeconds;

    const buyInNumber = Number(buyIn || cap);
    const ready =
        (!useCustomClose || customValid) &&
        text.trim().length >= 3 &&
        (kind !== 'field' || entrantIds.length >= 2) &&
        (kind !== 'free_for_all' || (buyInNumber >= MINIMUM_STAKE && buyInNumber <= cap));

    if (!hangout.data) {
        return (
            <Stack spacing={2}>
                <Typography variant="h2">Nothing to bet on</Typography>
                <Typography color="text.secondary">
                    No hangout is running yet.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack spacing={2.5}>
            <Typography variant="h2">What&rsquo;s the bet?</Typography>

            <TextField
                value={text}
                onChange={(event) => setText(event.target.value)}
                placeholder="Dave catches the egg one-handed"
                multiline
                minRows={2}
                autoFocus
                autoCapitalize="sentences"
            />

            <Box>
                <ToggleButtonGroup
                    value={kind}
                    exclusive
                    onChange={(_event, value: MarketKind | null) => value && setKind(value)}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1,
                        mt: 0.5,
                        '& .MuiToggleButton-root': {
                            borderRadius: '3px !important',
                            border: `1px solid ${colors.line} !important`,
                            textTransform: 'none',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            py: 1,
                        },
                    }}
                >
                    {KINDS.map((option) => (
                        <ToggleButton key={option.value} value={option.value}>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                {option.label}
                            </Typography>
                            <Typography
                                sx={{ fontSize: '0.72rem', color: colors.muted, textAlign: 'left' }}
                            >
                                {option.hint}
                            </Typography>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {kind === 'field' && (
                <Box>
                    <Typography variant="overline" sx={{ color: colors.muted }}>
                        Who could win
                    </Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {others.map((row) => (
                            <Chip
                                key={row.userId}
                                label={row.handle}
                                onClick={() =>
                                    setEntrantIds((current) =>
                                        current.includes(row.userId)
                                            ? current.filter((id) => id !== row.userId)
                                            : [...current, row.userId],
                                    )
                                }
                                sx={{
                                    bgcolor: entrantIds.includes(row.userId)
                                        ? colors.safety
                                        : colors.raised,
                                    color: entrantIds.includes(row.userId)
                                        ? colors.ground
                                        : colors.ink,
                                }}
                            />
                        ))}
                    </Stack>
                    {entrantIds.length < 2 && (
                        <Typography variant="body2" sx={{ color: colors.muted, mt: 0.5 }}>
                            Pick at least two.
                        </Typography>
                    )}
                </Box>
            )}

            {kind === 'free_for_all' && (
                <TextField
                    label="Everyone puts in"
                    value={buyIn}
                    onChange={(event) => setBuyIn(event.target.value.replace(/\D/g, ''))}
                    type="tel"
                    inputMode="numeric"
                    placeholder={String(Math.min(500, cap))}
                    error={buyIn !== '' && buyInNumber > cap}
                    helperText={`Up to ${formatTokens(cap)}, so nobody gets priced out`}
                />
            )}

            <Box>
                <Typography variant="overline" sx={{ color: colors.muted }}>
                    Betting closes in
                </Typography>
                <ToggleButtonGroup
                    value={useCustomClose ? 'custom' : closesInSeconds}
                    exclusive
                    onChange={(_event, value: number | 'custom' | null) => {
                        if (value === null) return;
                        if (value === 'custom') {
                            setUseCustomClose(true);
                            if (!customClose) {
                                // Seeded an hour out, so the picker opens on
                                // something plausible rather than on now.
                                setCustomClose(
                                    toLocalInputValue(new Date(Date.now() + 3_600_000)),
                                );
                            }
                            return;
                        }
                        setUseCustomClose(false);
                        setClosesIn(value);
                    }}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 1,
                        mt: 0.5,
                        '& .MuiToggleButton-root': {
                            borderRadius: '3px !important',
                            border: `1px solid ${colors.line} !important`,
                            textTransform: 'none',
                            fontFamily: mono,
                            fontSize: '0.8rem',
                        },
                    }}
                >
                    {CLOSE_PRESETS.map((preset) => (
                        <ToggleButton key={preset.seconds} value={preset.seconds}>
                            {preset.label}
                        </ToggleButton>
                    ))}
                    <ToggleButton value="custom" sx={{ gridColumn: '1 / -1' }}>
                        Pick a day and time
                    </ToggleButton>
                </ToggleButtonGroup>

                {useCustomClose && (
                    <TextField
                        type="datetime-local"
                        value={customClose}
                        onChange={(event) => setCustomClose(event.target.value)}
                        sx={{ mt: 1 }}
                        inputProps={{ min: toLocalInputValue(new Date(Date.now() + 60_000)) }}
                        error={customClose !== '' && !customValid}
                        helperText={
                            customClose === ''
                                ? 'When betting shuts'
                                : customSeconds < 30
                                  ? 'Pick a time in the future'
                                  : customSeconds > MAX_CLOSE_SECONDS
                                    ? 'Thirty days is the longest a bet can run'
                                    : `Betting shuts ${new Date(customClose).toLocaleString()}`
                        }
                    />
                )}
            </Box>

            {create.isError && <Alert severity="error">{getErrorMessage(create.error)}</Alert>}

            <Button
                variant="contained"
                size="large"
                disabled={!ready || create.isPending}
                onClick={() =>
                    create.mutate(
                        {
                            kind,
                            question: text.trim(),
                            closesInSeconds: effectiveClose,
                            entrantIds: kind === 'field' ? entrantIds : undefined,
                            buyIn: kind === 'free_for_all' ? buyInNumber : undefined,
                            minParticipants: kind === 'free_for_all' ? 2 : undefined,
                            joinDeadlineSeconds:
                                kind === 'free_for_all' ? effectiveClose : undefined,
                        },
                        { onSuccess: (market) => navigate(`/markets/${market.id}`) },
                    )
                }
            >
                {create.isPending ? 'Posting…' : 'Post it'}
            </Button>

        </Stack>
    );
}
